import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';
import { Database } from '@/integrations/supabase/types';

type SharedPlaylist = Database['public']['Tables']['shared_playlists']['Row'];

export function useSharedPlaylists() {
  const [playlists, setPlaylists] = useState<SharedPlaylist[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPlaylists = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('shared_playlists')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPlaylists(data as SharedPlaylist[] || []);
    } catch (error) {
      logger.error('Failed to fetch shared playlists', error);
      toast.error('Erro ao carregar playlists');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sharePlaylist = useCallback(async (
    name: string,
    description: string,
    tracks: any[],
    runContext?: any
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('shared_playlists')
        .insert({
          user_id: user.id,
          name,
          description,
          tracks,
          run_context: runContext,
          is_public: true,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Playlist compartilhada!');
      logger.info('Playlist shared', { playlistId: data.id });
      
      return data;
    } catch (error) {
      logger.error('Failed to share playlist', error);
      toast.error('Erro ao compartilhar playlist');
      throw error;
    }
  }, []);

  const likePlaylist = useCallback(async (playlistId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('shared_playlist_likes')
        .insert({ playlist_id: playlistId, user_id: user.id });

      if (error) throw error;

      // Fetch and increment likes count
      const { data: currentPlaylist } = await supabase
        .from('shared_playlists')
        .select('likes_count')
        .eq('id', playlistId)
        .single();

      const { error: updateError } = await supabase
        .from('shared_playlists')
        .update({ likes_count: (currentPlaylist?.likes_count || 0) + 1 })
        .eq('id', playlistId);

      if (updateError) throw updateError;

      toast.success('Playlist curtida!');
      await fetchPlaylists();
    } catch (error) {
      logger.error('Failed to like playlist', error);
      toast.error('Erro ao curtir playlist');
    }
  }, [fetchPlaylists]);

  const getPlaylistByCode = useCallback(async (shareCode: string) => {
    try {
      const { data, error } = await supabase
        .from('shared_playlists')
        .select('*')
        .eq('share_code', shareCode)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Failed to get playlist by code', error);
      toast.error('Playlist não encontrada');
      return null;
    }
  }, []);

  return {
    playlists,
    isLoading,
    fetchPlaylists,
    sharePlaylist,
    likePlaylist,
    getPlaylistByCode,
  };
}
